"use client";

import { Building2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";

import { OrganizationData } from "@/entities/organization/model/types";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { EmptyState } from "@/widgets/shared/empty-state";
import { OrganizationCard } from "./organization-card";

interface OrganizationGridProps {
  organizations: OrganizationData[];
  onLoadMore?: (() => void) | undefined;
  hasMore?: boolean | undefined;
}

export function OrganizationGrid({
  organizations,
  onLoadMore,
  hasMore,
}: OrganizationGridProps) {
  const tUi = useTranslations("Ui");
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: "200px",
  });

  const didTriggerRef = useRef(false);

  useEffect(() => {
    if (!isIntersecting) {
      didTriggerRef.current = false;
      return;
    }
    if (!hasMore || !onLoadMore) return;
    if (didTriggerRef.current) return;
    didTriggerRef.current = true;
    onLoadMore();
  }, [isIntersecting, hasMore, onLoadMore]);

  if (organizations.length === 0) {
    return (
      <EmptyState
        title={tUi("organizationGrid.organizatsiiNeNaydeny")}
        description={tUi("organizationGrid.poprobuyteIzmenitParametryPoiska")}
        icon={<Building2 className="h-12 w-12 text-muted-foreground" />}
      />
    );
  }

  return (
    <div className="pt-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {organizations.map((org) => (
          <OrganizationCard key={org.id} organization={org} />
        ))}
      </div>
      {onLoadMore && <div ref={ref} className="h-6" />}
    </div>
  );
}
